// API Service for Agencia PPC
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fallback data (used if API fails)
import { images as fallbackImages, translations as fallbackTranslations, contactInfo as fallbackContact, sectors as fallbackSectors, whatsappNumber as fallbackWhatsapp, phoneNumber as fallbackPhone, socialLinks as fallbackSocial } from '../data/mock';

class ApiService {
  constructor() {
    this.cache = {
      config: null,
      translations: null,
      lastFetch: null
    };
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache
  }

  async fetchWithFallback(endpoint, fallbackData) {
    try {
      const response = await fetch(`${API_URL}/api${endpoint}`);
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.warn(`API fetch failed for ${endpoint}, using fallback:`, error.message);
      return fallbackData;
    }
  }

  isCacheValid() {
    return this.cache.lastFetch && (Date.now() - this.cache.lastFetch < this.cacheTimeout);
  }

  async getSiteConfig() {
    if (this.isCacheValid() && this.cache.config) {
      return this.cache.config;
    }

    const fallback = {
      images: fallbackImages,
      contact: fallbackContact,
      socialLinks: fallbackSocial,
      whatsappNumber: fallbackWhatsapp,
      phoneNumber: fallbackPhone,
      sectors: fallbackSectors
    };

    const config = await this.fetchWithFallback('/content/config', fallback);
    this.cache.config = config;
    this.cache.lastFetch = Date.now();
    return config;
  }

  async getTranslations() {
    if (this.isCacheValid() && this.cache.translations) {
      return this.cache.translations;
    }

    const translations = await this.fetchWithFallback('/content/translations', fallbackTranslations);
    this.cache.translations = translations;
    this.cache.lastFetch = Date.now();
    return translations;
  }

  async getTranslationByLanguage(language) {
    const translations = await this.getTranslations();
    return translations[language] || translations.es;
  }

  async getAllSiteData() {
    if (this.isCacheValid() && this.cache.config && this.cache.translations) {
      return {
        config: this.cache.config,
        translations: this.cache.translations
      };
    }

    const fallback = {
      config: {
        images: fallbackImages,
        contact: fallbackContact,
        socialLinks: fallbackSocial,
        whatsappNumber: fallbackWhatsapp,
        phoneNumber: fallbackPhone,
        sectors: fallbackSectors
      },
      translations: fallbackTranslations
    };

    const data = await this.fetchWithFallback('/content/all', fallback);
    this.cache.config = data.config;
    this.cache.translations = data.translations;
    this.cache.lastFetch = Date.now();
    return data;
  }

  async submitLead(leadData) {
    try {
      const response = await fetch(`${API_URL}/api/leads/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(leadData),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit lead: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Lead submission failed:', error);
      throw error;
    }
  }

  clearCache() {
    this.cache = {
      config: null,
      translations: null,
      lastFetch: null
    };
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;

// Export helper functions
export const getSiteConfig = () => apiService.getSiteConfig();
export const getTranslations = () => apiService.getTranslations();
export const getTranslationByLanguage = (lang) => apiService.getTranslationByLanguage(lang);
export const getAllSiteData = () => apiService.getAllSiteData();
export const submitLead = (data) => apiService.submitLead(data);
